import { proStudioUrqlGql } from '@pypestream/api-services';
import { useEffect, useRef } from 'react';

import { tableStore } from '../stores/table-store';
import { shouldEjectOnDeletedVersion } from '../utils';
import { sendProStudioEvent } from '../xstate/app.xstate';

/**
 * PSTUDIO-3671: always-on, backend-anchored detection that the OPEN solution
 * version was deleted — the piece missing from the branch's other triggers.
 *
 * The same-browser fast paths (`useSolutionDeletedSync` /
 * `useCrossTabNodeSync`) ride BroadcastChannel and never cross machines; the
 * `botFlow` load guard only fires on a manual reload; and
 * `useCoworkerFocusSubscription` only subscribes while ANOTHER user holds an
 * active cell focus. So a user who simply has the fully-loaded editor open on a
 * different machine is never told when someone else deletes the solution — they
 * sit on stale data and the next autosave hits the deleted `solutionVersionId`
 * (the PSTUDIO-3669 FK-violation family).
 *
 * This watchdog closes that gap: whenever a solution version is open it holds a
 * lightweight `solutionVersionSubscription` purely to observe existence. On the
 * first non-null push it arms `hasSeenVersionData`; a subsequent null (the
 * deletion push Hasura emits for a removed `by_pk` row) then dispatches
 * `solutionNotFound` (toast + redirect to All Solutions, via the machine's
 * global `#solutionNotFound`). A null that was never preceded by data is a
 * still-provisioning transient — never an eject (see `shouldEjectOnDeletedVersion`).
 *
 * It deliberately does NOT merge payload data — live co-editing stays owned by
 * `useCoworkerFocusSubscription`; this hook only watches whether the version
 * still exists, so a second (cheap) subscription while a coworker is focused is
 * acceptable.
 */
export function useSolutionExistenceSubscription(): void {
  const { solutionVersionId } = tableStore;
  const hasSeenVersionDataRef = useRef(false);

  useEffect(() => {
    if (!solutionVersionId) return;

    // Fresh subscription for this version: nothing seen yet.
    hasSeenVersionDataRef.current = false;

    let unsubscribe: (() => void) | undefined;
    try {
      const { subscribe } = proStudioUrqlGql.solutionVersionSubscription({
        solutionVersionId,
      });

      ({ unsubscribe } = subscribe((subData) => {
        if (subData.error) return;
        // PSTUDIO-3953: this callback races the effect's own cleanup — urql can
        // deliver a message that was already queued when the version changed.
        // The branch below EJECTS the user (toast + redirect), so acting on a
        // late null for a version they have already left would throw them out of
        // the solution they are currently working in. The same gate is on the
        // other two ejecting paths (`reload-nodes`, `useCoworkerFocusSubscription`);
        // this hook is the third and is the ALWAYS-ON deleted-version detector,
        // so leaving it ungated would have kept the defect reachable by the very
        // path most likely to fire.
        if (tableStore.solutionVersionId !== solutionVersionId) return;
        const versionData =
          subData.data?.tenancy_solution_versions_ps_by_pk ?? null;

        if (
          shouldEjectOnDeletedVersion({
            hasSeenVersionData: hasSeenVersionDataRef.current,
            versionData,
          })
        ) {
          sendProStudioEvent({ type: 'solutionNotFound' });
          return;
        }

        if (versionData) {
          hasSeenVersionDataRef.current = true;
        }
      }));
    } catch (_err) {
      // A failed subscribe must never break the editor — the load-time guard
      // and the same-browser fast paths still apply.
    }

    return () => {
      unsubscribe?.();
    };
  }, [solutionVersionId]);
}
